import { CSSProperties, FC, useEffect, useRef, useState } from 'react'

// TERMINAL CROSSING — TOS easter egg, Frogger variant.
// You're a dock worker on the office side of the terminal trying to reach
// the docked container ships. To get there you cross the access road
// (terminal trucks, straddle carriers), the rail yard (freight trains),
// and the harbor traffic (tugboats, pontoons, barges). Fill all 5 berths
// to clear the shift.

const W = 480
const H = 720
const COLS = 12
const ROWS = 13
const CELL = W / COLS
const GRID_TOP = 80

const ROW_HOME = 0
const ROW_HARBOR_START = 1
const ROW_HARBOR_END = 4
const ROW_SAFE_MID = 5
const ROW_RAIL = 6
const ROW_SAFE_LOWER = 7
const ROW_ROAD_START = 8
const ROW_ROAD_END = 11
const ROW_START = 12

const HOP_FRAMES = 6

const STARTING_LIVES = 3
const TIME_PER_CROSSING_FRAMES = 60 * 30
const SCORE_PER_HOP_FORWARD = 10
const SCORE_BERTH_BASE = 200
const SCORE_TIME_BONUS = 5

const COMPANY_NAME = 'TERMINAL OS'

type WindowWithWebkitAudio = Window & { webkitAudioContext?: typeof AudioContext }

type LaneKind = 'road' | 'rail' | 'harbor'
type VehicleType =
  | 'truck'
  | 'agv'
  | 'straddle'
  | 'stacker'
  | 'train'
  | 'tugboat'
  | 'pontoon'
  | 'barge'

interface Vehicle {
  x: number
}

interface Lane {
  kind: LaneKind
  row: number
  type: VehicleType
  width: number
  speed: number
  dir: number
  spacing: number
  vehicles: Vehicle[]
}

type Outcome = { kind: 'safe' } | { kind: 'splat' } | { kind: 'berth'; idx: number }

interface Worker {
  col: number
  row: number
  x: number
  y: number
  fromX: number
  fromY: number
  toX: number
  toY: number
  hopFrames: number
  hopDuration: number
  facing: number
  pendingOutcome: Outcome | null
  dead: boolean
  deathFrames: number
}

interface Floater {
  x: number
  y: number
  text: string
  color: string
  life: number
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  color: string
  size: number
}

type GameStatus = 'ready' | 'playing' | 'shiftCleared' | 'gameover'

interface GameState {
  lanes: Lane[]
  level: number
  worker: Worker
  berthsFilled: boolean[]
  score: number
  hi: number
  lives: number
  timeRemaining: number
  status: GameStatus
  time: number
  floaters: Floater[]
  particles: Particle[]
  shake: number
  bestRowReached: number
  shiftClearedFrames: number
}

interface HudState {
  score: number
  hi: number
  lives: number
  level: number
  status: GameStatus
  muted: boolean
  berthsFilled: number
  time: number
}

interface TerminalCrossingProps {
  onClose?: () => void
}

function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min
}

class GameAudio {
  ctx: AudioContext | null = null
  masterGain: GainNode | null = null
  muted = false

  init(): void {
    if (this.ctx) return
    try {
      const w = window as WindowWithWebkitAudio
      const Ctor = window.AudioContext || w.webkitAudioContext
      if (!Ctor) return
      this.ctx = new Ctor()
      this.masterGain = this.ctx.createGain()
      this.masterGain.gain.value = 0.22
      this.masterGain.connect(this.ctx.destination)
    } catch {
      // Audio is non-essential; ignore unavailable AudioContext.
    }
  }

  private envelope(node: GainNode, attack: number, decay: number, peak = 1): void {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    node.gain.setValueAtTime(0, t)
    node.gain.linearRampToValueAtTime(peak, t + attack)
    node.gain.exponentialRampToValueAtTime(0.001, t + attack + decay)
  }

  hop(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'square'
    osc.frequency.setValueAtTime(440, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(660, this.ctx.currentTime + 0.05)
    this.envelope(gain, 0.001, 0.06, 0.1)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.07)
  }

  splat(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(220, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(60, this.ctx.currentTime + 0.4)
    this.envelope(gain, 0.005, 0.4, 0.3)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.45)
  }

  berthFilled(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const ctxLocal = this.ctx
    const masterLocal = this.masterGain
    ;[523, 659, 784, 1047].forEach((freq, i) => {
      const osc = ctxLocal.createOscillator()
      const gain = ctxLocal.createGain()
      osc.type = 'triangle'
      osc.frequency.value = freq
      const t = ctxLocal.currentTime + i * 0.07
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.18, t + 0.005)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.16)
      osc.connect(gain).connect(masterLocal)
      osc.start(t)
      osc.stop(t + 0.18)
    })
  }

  win(): void {
    if (!this.ctx || !this.masterGain || this.muted) return
    const ctxLocal = this.ctx
    const masterLocal = this.masterGain
    ;[523, 659, 784, 1047, 1318, 1568].forEach((freq, i) => {
      const osc = ctxLocal.createOscillator()
      const gain = ctxLocal.createGain()
      osc.type = 'triangle'
      osc.frequency.value = freq
      const t = ctxLocal.currentTime + i * 0.1
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.2, t + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3)
      osc.connect(gain).connect(masterLocal)
      osc.start(t)
      osc.stop(t + 0.32)
    })
  }

  toggleMute(): void {
    this.muted = !this.muted
  }
}

function buildLevel(level: number): { lanes: Lane[]; level: number } {
  const lanes: Lane[] = []
  const speedMult = 1 + (level - 1) * 0.25

  function distribute(spacing: number): Vehicle[] {
    const span = W + spacing * 2
    const count = Math.max(2, Math.ceil(span / spacing))
    const vs: Vehicle[] = []
    for (let i = 0; i < count; i++) {
      vs.push({ x: -spacing + i * spacing })
    }
    return vs
  }

  interface RoadConfig {
    row: number
    type: VehicleType
    speed: number
    dir: number
    width: number
    spacing: number
  }
  const roadConfigs: RoadConfig[] = [
    { row: 8, type: 'truck', speed: 1.4 * speedMult, dir: 1, width: 80, spacing: 200 },
    { row: 9, type: 'agv', speed: 1.8 * speedMult, dir: -1, width: 60, spacing: 180 },
    { row: 10, type: 'straddle', speed: 1.0 * speedMult, dir: 1, width: 100, spacing: 240 },
    { row: 11, type: 'stacker', speed: 1.6 * speedMult, dir: -1, width: 90, spacing: 220 },
  ]

  for (const c of roadConfigs) {
    lanes.push({
      kind: 'road',
      row: c.row,
      type: c.type,
      width: c.width,
      speed: c.speed,
      dir: c.dir,
      spacing: c.spacing,
      vehicles: distribute(c.spacing),
    })
  }

  // Trains are long and rare — big gaps so the player can cross.
  lanes.push({
    kind: 'rail',
    row: 6,
    type: 'train',
    width: 240,
    speed: 2.2 * speedMult,
    dir: 1,
    spacing: 700,
    vehicles: [{ x: -200 }, { x: 500 }],
  })

  // Harbor lanes (rows 1-4) — same dodge mechanic as roads. We deliberately
  // dropped Frogger's ride-the-log mechanic because precision-timing on small
  // moving targets made the upper half effectively impassable.
  const harborConfigs: RoadConfig[] = [
    { row: 1, type: 'tugboat', speed: 1.7 * speedMult, dir: -1, width: 70, spacing: 220 },
    { row: 2, type: 'pontoon', speed: 1.2 * speedMult, dir: 1, width: 90, spacing: 240 },
    { row: 3, type: 'barge', speed: 0.9 * speedMult, dir: -1, width: 120, spacing: 280 },
    { row: 4, type: 'tugboat', speed: 2.0 * speedMult, dir: 1, width: 70, spacing: 240 },
  ]

  for (const c of harborConfigs) {
    lanes.push({
      kind: 'harbor',
      row: c.row,
      type: c.type,
      width: c.width,
      speed: c.speed,
      dir: c.dir,
      spacing: c.spacing,
      vehicles: distribute(c.spacing),
    })
  }

  return { lanes, level }
}

function updateLanes(lanes: Lane[]): void {
  for (const lane of lanes) {
    for (const v of lane.vehicles) {
      v.x += lane.dir * lane.speed
      if (lane.dir > 0 && v.x > W + 50) {
        v.x = -lane.width - rand(0, lane.spacing * 0.4)
      } else if (lane.dir < 0 && v.x + lane.width < -50) {
        v.x = W + 50 + rand(0, lane.spacing * 0.4)
      }
    }
  }
}

function vehicleScreenX(_lane: Lane, vehicle: Vehicle): number {
  return vehicle.x
}

function rowY(row: number): number {
  return GRID_TOP + row * CELL
}

function colX(col: number): number {
  return col * CELL
}

const TerminalCrossing: FC<TerminalCrossingProps> = ({ onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const stateRef = useRef<GameState | null>(null)
  const audioRef = useRef<GameAudio>(new GameAudio())
  const startGameRef = useRef<(() => void) | null>(null)
  const [hud, setHud] = useState<HudState>({
    score: 0,
    hi: 0,
    lives: STARTING_LIVES,
    level: 1,
    status: 'ready',
    muted: false,
    berthsFilled: 0,
    time: 1,
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const audio = audioRef.current

    const dpr = window.devicePixelRatio || 1
    const resize = (): void => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    let savedHi = 0
    try {
      savedHi = parseInt(localStorage.getItem('terminalCrossingHi') || '0', 10) || 0
    } catch {
      // localStorage may be unavailable; ignore.
    }

    const berthCols = [1, 3, 5, 7, 9]

    const initState = (level = 1): GameState => {
      const built = buildLevel(level)
      return {
        lanes: built.lanes,
        level,
        worker: {
          col: 5,
          row: ROW_START,
          x: colX(5) + CELL / 2,
          y: rowY(ROW_START) + CELL / 2,
          fromX: 0,
          fromY: 0,
          toX: 0,
          toY: 0,
          hopFrames: 0,
          hopDuration: HOP_FRAMES,
          facing: 0,
          pendingOutcome: null,
          dead: false,
          deathFrames: 0,
        },
        berthsFilled: berthCols.map(() => false),
        score: 0,
        hi: savedHi,
        lives: STARTING_LIVES,
        timeRemaining: TIME_PER_CROSSING_FRAMES,
        status: 'ready',
        time: 0,
        floaters: [],
        particles: [],
        shake: 0,
        bestRowReached: ROW_START,
        shiftClearedFrames: 0,
      }
    }

    stateRef.current = initState()

    const startGame = (): void => {
      const s = initState(1)
      s.status = 'playing'
      stateRef.current = s
      setHud({
        score: 0,
        hi: s.hi,
        lives: STARTING_LIVES,
        level: 1,
        status: 'playing',
        muted: audio.muted,
        berthsFilled: 0,
        time: 1,
      })
    }
    startGameRef.current = startGame

    const resetWorkerToStart = (): void => {
      const s = stateRef.current
      if (!s) return
      s.worker.col = 5
      s.worker.row = ROW_START
      s.worker.x = colX(5) + CELL / 2
      s.worker.y = rowY(ROW_START) + CELL / 2
      s.worker.hopFrames = 0
      s.worker.facing = 0
      s.worker.pendingOutcome = null
      s.worker.dead = false
      s.worker.deathFrames = 0
      s.bestRowReached = ROW_START
      s.timeRemaining = TIME_PER_CROSSING_FRAMES
    }

    const fillBerth = (berthIdx: number): boolean => {
      const s = stateRef.current
      if (!s) return false
      if (s.berthsFilled[berthIdx]) return false
      s.berthsFilled[berthIdx] = true
      const filledCount = s.berthsFilled.filter(Boolean).length
      const base = SCORE_BERTH_BASE * filledCount
      const timeBonus = Math.floor(s.timeRemaining / 60) * SCORE_TIME_BONUS * 60
      s.score += base + timeBonus
      s.floaters.push({
        x: colX(berthCols[berthIdx]) + CELL / 2,
        y: rowY(ROW_HOME) + CELL / 2,
        text: `+${base + timeBonus} BERTH`,
        color: '#ffd166',
        life: 90,
      })
      audio.berthFilled()
      resetWorkerToStart()
      if (s.berthsFilled.every(Boolean)) {
        const levelBonus = 1000 * s.level
        s.score += levelBonus
        s.floaters.push({
          x: W / 2,
          y: H / 2,
          text: `+${levelBonus} SHIFT BONUS`,
          color: '#ffd166',
          life: 120,
        })
        audio.win()
        s.status = 'shiftCleared'
        s.shiftClearedFrames = 180
        if (s.score > s.hi) {
          s.hi = s.score
          try {
            localStorage.setItem('terminalCrossingHi', String(s.hi))
          } catch {
            // localStorage may be unavailable; ignore.
          }
        }
        setHud(p => ({ ...p, status: 'shiftCleared', score: s.score, hi: s.hi, berthsFilled: 5 }))
      } else {
        setHud(p => ({ ...p, score: s.score, berthsFilled: filledCount }))
      }
      return true
    }

    const killWorker = (): void => {
      const s = stateRef.current
      if (!s) return
      if (s.worker.dead) return
      s.worker.dead = true
      s.worker.deathFrames = 60
      s.lives--
      s.shake = 8
      audio.splat()
      for (let i = 0; i < 18; i++) {
        s.particles.push({
          x: s.worker.x,
          y: s.worker.y,
          vx: rand(-4, 4),
          vy: rand(-4, 1),
          life: rand(20, 40),
          color: '#ffd166',
          size: rand(1.5, 3),
        })
      }
      s.floaters.push({
        x: s.worker.x,
        y: s.worker.y - 16,
        text: 'STRUCK',
        color: '#d94a4a',
        life: 60,
      })
    }

    const advanceLevel = (): void => {
      const s = stateRef.current
      if (!s) return
      const newLevel = s.level + 1
      const carryScore = s.score
      const carryLives = s.lives
      const carryHi = s.hi
      const fresh = initState(newLevel)
      Object.assign(s, fresh)
      s.score = carryScore
      s.lives = carryLives
      s.hi = carryHi
      s.status = 'playing'
      setHud(p => ({
        ...p,
        status: 'playing',
        level: newLevel,
        berthsFilled: 0,
      }))
    }

    // CRITICAL: resolve hop outcome NOW using current vehicle positions, not
    // when the hop animation finishes. Otherwise barges drift during the hop
    // and the worker can land in water even though they aimed at a barge that
    // WAS there when they pressed UP.
    const computeLandingOutcome = (s: GameState, row: number, x: number): Outcome => {
      if (row === ROW_HOME) {
        const idx = berthCols.indexOf(Math.round((x - CELL / 2) / CELL))
        if (idx >= 0) return { kind: 'berth', idx }
        return { kind: 'splat' }
      }
      if (
        (row >= ROW_HARBOR_START && row <= ROW_HARBOR_END) ||
        (row >= ROW_ROAD_START && row <= ROW_ROAD_END) ||
        row === ROW_RAIL
      ) {
        const lane = s.lanes.find(l => l.row === row)
        if (!lane) return { kind: 'safe' }
        for (const v of lane.vehicles) {
          const sx = vehicleScreenX(lane, v)
          if (x >= sx - 2 && x <= sx + lane.width + 2) {
            return { kind: 'splat' }
          }
        }
        return { kind: 'safe' }
      }
      return { kind: 'safe' }
    }

    const tryHop = (dCol: number, dRow: number, facing: number): void => {
      const s = stateRef.current
      if (!s) return
      const w = s.worker
      if (w.dead || w.hopFrames > 0) return
      const newCol = w.col + dCol
      const newRow = w.row + dRow
      if (newCol < 0 || newCol >= COLS) return
      if (newRow < 0 || newRow >= ROWS) return
      w.fromX = w.x
      w.fromY = w.y
      w.toX = colX(newCol) + CELL / 2
      w.toY = rowY(newRow) + CELL / 2
      w.hopFrames = w.hopDuration
      w.col = newCol
      w.row = newRow
      w.facing = facing
      w.pendingOutcome = computeLandingOutcome(s, newRow, w.toX)
      if (newRow < s.bestRowReached) {
        s.score += SCORE_PER_HOP_FORWARD
        s.bestRowReached = newRow
      }
      audio.hop()
    }

    const onKeyDown = (e: KeyboardEvent): void => {
      audio.init()
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
        e.preventDefault()
      }
      if (e.key === 'Escape' && onClose) onClose()
      const s = stateRef.current
      if (!s) return
      if (
        s.status === 'ready' &&
        (e.key === ' ' || /^Arrow/.test(e.key) || /^[wasdWASD]$/.test(e.key))
      ) {
        startGame()
        return
      }
      if (s.status === 'playing' && !s.worker.dead && !e.repeat) {
        if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') tryHop(0, -1, 0)
        else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') tryHop(1, 0, 1)
        else if (e.key === 'ArrowDown' || e.key === 's' || e.key === 'S') tryHop(0, 1, 2)
        else if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') tryHop(-1, 0, 3)
      }
      if (e.key === 'm' || e.key === 'M') {
        audio.toggleMute()
        setHud(p => ({ ...p, muted: audio.muted }))
      }
      if (e.key === 'r' || e.key === 'R') {
        const cur = s.status
        if (cur === 'gameover' || cur === 'ready') startGame()
        else if (cur === 'playing') {
          if (window.confirm('Restart current shift? Your progress will be lost.')) startGame()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)

    const detectAndApplyCollisions = (s: GameState): void => {
      const w = s.worker
      if (w.dead) return
      if (w.hopFrames > 0) return

      const outcome = w.pendingOutcome
      w.pendingOutcome = null

      if (!outcome || outcome.kind === 'safe') return
      if (outcome.kind === 'berth') {
        fillBerth(outcome.idx)
        return
      }
      if (outcome.kind === 'splat') {
        killWorker()
        return
      }
    }

    let animationId = 0

    const draw = (): void => {
      const s = stateRef.current
      if (!s) {
        animationId = requestAnimationFrame(draw)
        return
      }
      s.time++

      const screenW = canvas.width / dpr
      const screenH = canvas.height / dpr
      const scale = Math.min(screenW / W, screenH / H, 2.0)
      const offsetX = (screenW - W * scale) / 2
      const offsetY = (screenH - H * scale) / 2

      let shakeX = 0
      let shakeY = 0
      if (s.shake > 0) {
        shakeX = rand(-s.shake, s.shake)
        shakeY = rand(-s.shake, s.shake)
        s.shake *= 0.85
        if (s.shake < 0.3) s.shake = 0
      }
      ctx.setTransform(dpr, 0, 0, dpr, shakeX * dpr, shakeY * dpr)

      ctx.fillStyle = '#080e18'
      ctx.fillRect(0, 0, screenW, screenH)

      ctx.save()
      ctx.translate(offsetX, offsetY)
      ctx.scale(scale, scale)

      if (s.status === 'playing') {
        // Vehicles must move BEFORE worker logic so collision uses fresh positions.
        updateLanes(s.lanes)

        const w = s.worker
        if (w.hopFrames > 0) {
          w.hopFrames--
          const t = 1 - w.hopFrames / w.hopDuration
          w.x = w.fromX + (w.toX - w.fromX) * t
          w.y = w.fromY + (w.toY - w.fromY) * t
          if (w.hopFrames === 0) {
            detectAndApplyCollisions(s)
          }
        } else if (!w.dead) {
          // Stationary on a hazard row — vehicles still hit you. Matches the
          // arcade rule: you can't camp between vehicles forever.
          const row = w.row
          const isHazardRow =
            (row >= ROW_HARBOR_START && row <= ROW_HARBOR_END) ||
            (row >= ROW_ROAD_START && row <= ROW_ROAD_END) ||
            row === ROW_RAIL
          if (isHazardRow) {
            const lane = s.lanes.find(l => l.row === row)
            if (lane) {
              for (const v of lane.vehicles) {
                const sx = vehicleScreenX(lane, v)
                if (w.x >= sx - 2 && w.x <= sx + lane.width + 2) {
                  killWorker()
                  break
                }
              }
            }
          }
        }

        if (w.dead) {
          w.deathFrames--
          if (w.deathFrames <= 0) {
            if (s.lives < 0) {
              s.status = 'gameover'
              if (s.score > s.hi) {
                s.hi = s.score
                try {
                  localStorage.setItem('terminalCrossingHi', String(s.hi))
                } catch {
                  // localStorage may be unavailable; ignore.
                }
              }
              setHud(p => ({ ...p, status: 'gameover', hi: s.hi }))
            } else {
              resetWorkerToStart()
            }
          }
        }

        if (!w.dead) {
          s.timeRemaining--
          if (s.timeRemaining <= 0) {
            killWorker()
            s.floaters.push({ x: w.x, y: w.y - 16, text: 'TIME!', color: '#d94a4a', life: 60 })
          }
        }

        setHud(prev => {
          const filledCount = s.berthsFilled.filter(Boolean).length
          const tFrac = s.timeRemaining / TIME_PER_CROSSING_FRAMES
          if (
            prev.score === s.score &&
            prev.lives === s.lives &&
            prev.berthsFilled === filledCount &&
            Math.abs(prev.time - tFrac) < 0.01 &&
            prev.status === s.status
          )
            return prev
          return {
            ...prev,
            score: s.score,
            lives: s.lives,
            berthsFilled: filledCount,
            time: tFrac,
            status: s.status,
          }
        })

        s.particles = s.particles.filter(p => {
          p.x += p.vx
          p.y += p.vy
          p.vy += 0.2
          p.vx *= 0.96
          p.life--
          return p.life > 0
        })
        s.floaters = s.floaters.filter(f => {
          f.y -= 0.4
          f.life--
          return f.life > 0
        })
      } else if (s.status === 'shiftCleared') {
        s.shiftClearedFrames--
        if (s.shiftClearedFrames <= 0) {
          advanceLevel()
        }
        s.particles = s.particles.filter(p => {
          p.x += p.vx
          p.y += p.vy
          p.vy += 0.2
          p.life--
          return p.life > 0
        })
        s.floaters = s.floaters.filter(f => {
          f.y -= 0.4
          f.life--
          return f.life > 0
        })
      }

      drawBackground(ctx, s)
      for (const lane of s.lanes) drawLane(ctx, lane, s.time)
      drawBerths(ctx, s.berthsFilled, berthCols, s.time)
      drawWorker(ctx, s.worker)

      s.particles.forEach(p => {
        const alpha = Math.min(1, p.life / 25)
        ctx.fillStyle = p.color
        ctx.globalAlpha = alpha
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size)
      })
      ctx.globalAlpha = 1

      s.floaters.forEach(f => {
        const alpha = Math.min(1, f.life / 30)
        ctx.fillStyle = f.color
        ctx.globalAlpha = alpha
        ctx.font = 'bold 11px ui-monospace, Menlo, monospace'
        ctx.textAlign = 'center'
        ctx.fillText(f.text, f.x, f.y)
      })
      ctx.globalAlpha = 1

      ctx.restore()

      const vignette = ctx.createRadialGradient(
        screenW / 2,
        screenH / 2,
        screenH * 0.4,
        screenW / 2,
        screenH / 2,
        screenH,
      )
      vignette.addColorStop(0, 'rgba(0,0,0,0)')
      vignette.addColorStop(1, 'rgba(0,0,0,0.5)')
      ctx.fillStyle = vignette
      ctx.fillRect(0, 0, screenW, screenH)

      animationId = requestAnimationFrame(draw)
    }
    animationId = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(animationId)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', resize)
    }
  }, [onClose])

  return (
    <div style={styles.root}>
      <div style={styles.hud}>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>SCORE</span>
          <span style={styles.hudValue}>{String(hud.score).padStart(6, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>BEST</span>
          <span style={styles.hudValue}>{String(hud.hi).padStart(6, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>SHIFT</span>
          <span style={styles.hudValue}>{String(hud.level).padStart(2, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>BERTHS</span>
          <span style={styles.hudValue}>{hud.berthsFilled} / 5</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>LIVES</span>
          <span style={styles.hudValue}>{'◉ '.repeat(Math.max(0, hud.lives))}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>TIME</span>
          <div style={styles.timeBar}>
            <div
              style={{
                ...styles.timeFill,
                width: `${Math.max(0, Math.min(1, hud.time)) * 100}%`,
                background: hud.time < 0.25 ? '#d94a4a' : hud.time < 0.5 ? '#d97a2c' : '#ffd166',
              }}
            />
          </div>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>AUDIO</span>
          <span style={styles.hudValue}>{hud.muted ? '○ MUTE' : '● ON'}</span>
        </div>
        <button
          style={styles.restart}
          onClick={() => {
            try {
              audioRef.current.init()
            } catch {
              // ignore audio init failures
            }
            if (startGameRef.current) startGameRef.current()
          }}
          onMouseDown={e => e.preventDefault()}
          title='Restart shift'
        >
          ↻ RESTART
        </button>
        {onClose && (
          <button style={styles.close} onClick={onClose}>
            ESC
          </button>
        )}
      </div>

      <canvas ref={canvasRef} style={styles.canvas} />

      {hud.status === 'ready' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>TERMINAL CROSSING</div>
          <div style={styles.overlaySubLarge}>
            cross the access road · cross the rail tracks · cross the harbor traffic · fill all 5
            berths
          </div>
          <div style={styles.legend}>
            <div style={styles.legendRow}>← → ↑ ↓ or WASD : hop one cell</div>
            <div style={styles.legendRow}>
              everything that moves will run you over — time the gaps
            </div>
            <div style={styles.legendRow}>land on a berth (top row) to load a ship</div>
            <div style={styles.legendRow}>
              don&apos;t stand still in a lane — vehicles WILL hit you
            </div>
          </div>
          <div style={styles.controls}>press SPACE or any direction to start</div>
        </div>
      )}

      {hud.status === 'shiftCleared' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>SHIFT CLEARED</div>
          <div style={styles.overlaySub}>
            all 5 berths loaded · score: {hud.score.toLocaleString()}
          </div>
          <div style={styles.overlaySub}>next shift incoming...</div>
        </div>
      )}

      {hud.status === 'gameover' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>SHIFT TERMINATED</div>
          <div style={styles.overlaySub}>
            final score: {hud.score.toLocaleString()}{' '}
            {hud.score >= hud.hi && hud.score > 0 ? '· NEW BEST' : ''}
          </div>
          <pre style={styles.report}>
            {`SHIFT REPORT — ${COMPANY_NAME}
operator down at shift ${hud.level}
${hud.berthsFilled} of 5 berths loaded this attempt
${hud.score >= hud.hi && hud.score > 0 ? 'operator commendation issued' : 'see you next shift'}`}
          </pre>
          <div style={styles.overlaySub}>press R or click RESTART to try again · ESC to close</div>
        </div>
      )}
    </div>
  )
}

export default TerminalCrossing

// ============= DRAWING =============

function drawBackground(ctx: CanvasRenderingContext2D, s: GameState): void {
  for (let r = 0; r < ROWS; r++) {
    const y = rowY(r)
    let color = '#1a2436'
    if (r === ROW_HOME) color = '#0d1f3a'
    else if (r >= ROW_HARBOR_START && r <= ROW_HARBOR_END) color = '#15253d'
    else if (r === ROW_SAFE_MID) color = '#2a3548'
    else if (r === ROW_RAIL) color = '#1d1815'
    else if (r === ROW_SAFE_LOWER) color = '#2a3548'
    else if (r >= ROW_ROAD_START && r <= ROW_ROAD_END) color = '#1a1f28'
    else if (r === ROW_START) color = '#3a4458'
    ctx.fillStyle = color
    ctx.fillRect(0, y, W, CELL)
  }

  ctx.strokeStyle = 'rgba(120, 170, 220, 0.12)'
  ctx.lineWidth = 0.7
  const waveT = s.time * 0.04
  for (let r = ROW_HARBOR_START; r <= ROW_HARBOR_END; r++) {
    const yBase = rowY(r)
    for (let i = 0; i < 4; i++) {
      const yOff = i * (CELL / 4)
      ctx.beginPath()
      for (let x = 0; x < W; x += 8) {
        const wave = Math.sin(waveT + x * 0.05 + r * 0.7 + i * 0.3) * 1.2
        if (x === 0) ctx.moveTo(x, yBase + yOff + wave)
        else ctx.lineTo(x, yBase + yOff + wave)
      }
      ctx.stroke()
    }
  }

  ctx.strokeStyle = 'rgba(255, 209, 102, 0.3)'
  ctx.lineWidth = 1
  ctx.setLineDash([8, 8])
  for (let r = ROW_ROAD_START + 1; r <= ROW_ROAD_END; r++) {
    const y = rowY(r)
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(W, y)
    ctx.stroke()
  }
  ctx.setLineDash([])

  const railY = rowY(ROW_RAIL)
  ctx.fillStyle = '#3a2c20'
  for (let x = 0; x < W; x += 14) {
    ctx.fillRect(x, railY + 4, 8, CELL - 8)
  }
  ctx.strokeStyle = '#7a7a7a'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(0, railY + 12)
  ctx.lineTo(W, railY + 12)
  ctx.moveTo(0, railY + CELL - 12)
  ctx.lineTo(W, railY + CELL - 12)
  ctx.stroke()

  ctx.fillStyle = 'rgba(255, 209, 102, 0.4)'
  ctx.font = 'bold 8px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.fillText('INSPECTION DECK', W / 2, rowY(ROW_SAFE_LOWER) + CELL / 2 + 3)
  ctx.fillText('STAGING PIER', W / 2, rowY(ROW_SAFE_MID) + CELL / 2 + 3)
}

function drawLane(ctx: CanvasRenderingContext2D, lane: Lane, time: number): void {
  for (const v of lane.vehicles) {
    const sx = vehicleScreenX(lane, v)
    const y = rowY(lane.row)
    if (sx + lane.width < -50 || sx > W + 50) continue

    if (lane.kind === 'harbor') {
      drawHarborVehicle(ctx, lane, v, sx, y, time)
    } else if (lane.kind === 'rail') {
      drawTrain(ctx, lane, sx, y)
    } else {
      drawRoadVehicle(ctx, lane, sx, y)
    }
  }
}

function drawRoadVehicle(ctx: CanvasRenderingContext2D, lane: Lane, sx: number, y: number): void {
  const w = lane.width
  const h = CELL - 8
  const yTop = y + 4

  if (lane.type === 'truck') {
    ctx.fillStyle = '#1a2236'
    ctx.fillRect(sx, yTop + 2, w, h - 4)
    const cw = w * 0.65
    const cx = sx + (lane.dir > 0 ? 0 : w - cw)
    ctx.fillStyle = '#3a8fd6'
    ctx.fillRect(cx, yTop, cw, h)
    ctx.fillStyle = 'rgba(255,255,255,0.18)'
    ctx.fillRect(cx, yTop, cw, 2)
    ctx.strokeStyle = 'rgba(0,0,0,0.4)'
    ctx.lineWidth = 0.6
    for (let i = 1; i < 8; i++) {
      const lx = cx + (cw / 8) * i
      ctx.beginPath()
      ctx.moveTo(lx, yTop + 2)
      ctx.lineTo(lx, yTop + h - 2)
      ctx.stroke()
    }
    const cabW = w * 0.3
    const cabX = sx + (lane.dir > 0 ? w - cabW : 0)
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(cabX, yTop + 4, cabW, h - 8)
    ctx.fillStyle = '#3a5a8a'
    ctx.fillRect(cabX + (lane.dir > 0 ? 2 : cabW - 14), yTop + 8, 12, 8)
    ctx.fillStyle = '#0a0a0a'
    ctx.fillRect(sx + 4, y + CELL - 6, 8, 4)
    ctx.fillRect(sx + w - 12, y + CELL - 6, 8, 4)
    ctx.fillRect(sx + w / 2 - 4, y + CELL - 6, 8, 4)
    ctx.fillStyle = lane.dir > 0 ? '#fff8c0' : '#d94a4a'
    if (lane.dir > 0) ctx.fillRect(sx + w - 2, yTop + 8, 2, 3)
    else ctx.fillRect(sx, yTop + 8, 2, 3)
  } else if (lane.type === 'agv') {
    ctx.fillStyle = '#3a3a3a'
    ctx.fillRect(sx, yTop + 4, w, h - 8)
    ctx.fillStyle = '#d97a2c'
    ctx.fillRect(sx + 4, yTop, w - 8, h - 4)
    ctx.fillStyle = 'rgba(255,255,255,0.18)'
    ctx.fillRect(sx + 4, yTop, w - 8, 2)
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(sx + 8, yTop - 2, w - 16, 2)
    ctx.fillStyle = '#0a0a0a'
    for (let i = 0; i < 4; i++) {
      ctx.fillRect(sx + 4 + i * (w / 4), y + CELL - 5, 5, 3)
    }
    ctx.fillStyle = `rgba(220, 60, 60, ${0.5 + Math.sin(Date.now() * 0.01) * 0.5})`
    ctx.fillRect(sx + w / 2 - 1, yTop - 4, 2, 2)
  } else if (lane.type === 'straddle') {
    ctx.fillStyle = '#5a4520'
    ctx.fillRect(sx + 6, yTop, 4, h)
    ctx.fillRect(sx + w - 10, yTop, 4, h)
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(sx + 7, yTop, 2, h)
    ctx.fillRect(sx + w - 9, yTop, 2, h)
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(sx + 4, yTop, w - 8, 4)
    ctx.fillStyle = '#3aa15a'
    ctx.fillRect(sx + 14, yTop + 8, w - 28, h - 14)
    ctx.fillStyle = 'rgba(255,255,255,0.18)'
    ctx.fillRect(sx + 14, yTop + 8, w - 28, 2)
    ctx.fillStyle = '#1a2236'
    const cabX = sx + (lane.dir > 0 ? w - 16 : 4)
    ctx.fillRect(cabX, yTop + 4, 12, 12)
    ctx.fillStyle = '#3a5a8a'
    ctx.fillRect(cabX + 2, yTop + 6, 8, 6)
    ctx.fillStyle = '#0a0a0a'
    ctx.fillRect(sx + 4, y + CELL - 6, 8, 4)
    ctx.fillRect(sx + w - 12, y + CELL - 6, 8, 4)
  } else if (lane.type === 'stacker') {
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(sx + 4, yTop + 4, w - 24, h - 8)
    ctx.fillStyle = '#1a1a1a'
    ctx.fillRect(sx + 4, yTop + h - 8, w - 24, 2)
    ctx.fillStyle = '#1a2236'
    ctx.fillRect(sx + 8, yTop, w - 32, h - 4)
    ctx.fillStyle = '#3a5a8a'
    ctx.fillRect(sx + 10, yTop + 2, w - 36, 8)
    const mastX = sx + (lane.dir > 0 ? w - 18 : 4)
    ctx.fillStyle = '#5a4520'
    ctx.fillRect(mastX, yTop, 4, h)
    const contX = sx + (lane.dir > 0 ? w - 20 : 0)
    ctx.fillStyle = '#a83a72'
    ctx.fillRect(contX, yTop + 6, 16, h - 10)
    ctx.fillStyle = `rgba(255, 209, 102, ${0.5 + Math.sin(Date.now() * 0.012) * 0.5})`
    ctx.fillRect(sx + w / 2 - 16, yTop - 3, 3, 3)
    ctx.fillStyle = '#0a0a0a'
    ctx.fillRect(sx + 6, y + CELL - 6, 8, 4)
    ctx.fillRect(sx + w - 28, y + CELL - 6, 8, 4)
  }
}

function drawTrain(ctx: CanvasRenderingContext2D, lane: Lane, sx: number, y: number): void {
  const w = lane.width
  const yTop = y + 4
  const h = CELL - 8
  const segLen = w / 5
  const engX = lane.dir > 0 ? sx + w - segLen : sx
  ctx.fillStyle = '#3a2c12'
  ctx.fillRect(engX, yTop, segLen, h)
  ctx.fillStyle = '#d94a4a'
  ctx.fillRect(engX + 2, yTop + 2, segLen - 4, h - 4)
  ctx.fillStyle = '#1a2236'
  ctx.fillRect(engX + (lane.dir > 0 ? segLen - 14 : 4), yTop + 4, 10, h - 8)
  ctx.fillStyle = '#3a5a8a'
  ctx.fillRect(engX + (lane.dir > 0 ? segLen - 12 : 6), yTop + 6, 6, 4)

  for (let i = 0; i < 4; i++) {
    const fx = lane.dir > 0 ? sx + i * segLen : sx + (i + 1) * segLen
    ctx.fillStyle = '#3a3a3a'
    ctx.fillRect(fx, yTop + 6, segLen - 2, h - 12)
    const colors = ['#3a8fd6', '#d97a2c', '#3aa15a', '#a83a72']
    ctx.fillStyle = colors[i % colors.length]
    ctx.fillRect(fx + 4, yTop + 2, segLen - 10, h - 10)
    ctx.fillStyle = 'rgba(255,255,255,0.18)'
    ctx.fillRect(fx + 4, yTop + 2, segLen - 10, 2)
    ctx.fillStyle = '#5a5a5a'
    ctx.fillRect(fx + segLen - 2, yTop + h / 2 - 1, 2, 2)
  }

  ctx.fillStyle = '#0a0a0a'
  for (let x = sx + 4; x < sx + w - 4; x += 16) {
    ctx.fillRect(x, y + CELL - 4, 4, 3)
  }

  ctx.fillStyle = '#fff8c0'
  if (lane.dir > 0) ctx.fillRect(sx + w - 2, yTop + h / 2 - 2, 2, 4)
  else ctx.fillRect(sx, yTop + h / 2 - 2, 2, 4)
}

function drawHarborVehicle(
  ctx: CanvasRenderingContext2D,
  lane: Lane,
  v: Vehicle,
  sx: number,
  y: number,
  time: number,
): void {
  const w = lane.width
  const h = CELL - 4
  const yTop = y + 2

  if (lane.type === 'barge') {
    ctx.fillStyle = '#3a2c20'
    ctx.fillRect(sx, yTop + h - 6, w, 6)
    ctx.fillStyle = 'rgba(255,255,255,0.1)'
    ctx.fillRect(sx, yTop + h - 6, w, 1)
    ctx.fillStyle = 'rgba(0,0,0,0.4)'
    ctx.fillRect(sx, yTop + h - 1, w, 1)
    const colors = ['#3a8fd6', '#d97a2c', '#3aa15a', '#a83a72', '#e6c14a']
    const containerW = (w - 8) / 3
    for (let i = 0; i < 3; i++) {
      const cx = sx + 4 + i * containerW
      ctx.fillStyle = colors[(i + Math.floor(v.x / 100)) % colors.length]
      ctx.fillRect(cx, yTop + 2, containerW - 2, h - 9)
      ctx.fillStyle = 'rgba(255,255,255,0.18)'
      ctx.fillRect(cx, yTop + 2, containerW - 2, 1.5)
      ctx.strokeStyle = 'rgba(0,0,0,0.5)'
      ctx.lineWidth = 0.6
      ctx.strokeRect(cx, yTop + 2, containerW - 2, h - 9)
    }
    ctx.fillStyle = '#c9a648'
    if (lane.dir > 0) ctx.fillRect(sx + w - 4, yTop + h - 8, 4, 2)
    else ctx.fillRect(sx, yTop + h - 8, 4, 2)
  } else if (lane.type === 'tugboat') {
    ctx.fillStyle = '#d94a4a'
    ctx.fillRect(sx + 4, yTop + h - 12, w - 8, 10)
    ctx.fillStyle = 'rgba(0,0,0,0.4)'
    ctx.fillRect(sx + 4, yTop + h - 4, w - 8, 2)
    ctx.fillStyle = '#1a2236'
    ctx.fillRect(sx + w / 2 - 8, yTop + 4, 16, 12)
    ctx.fillStyle = '#3a5a8a'
    ctx.fillRect(sx + w / 2 - 6, yTop + 6, 12, 6)
    ctx.fillStyle = '#3a3a3a'
    ctx.fillRect(sx + w / 2 - 2, yTop, 4, 6)
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(sx + w / 2 - 2, yTop, 4, 1)
    ctx.fillStyle = `rgba(180, 180, 180, ${0.3 + Math.sin(time * 0.1 + v.x) * 0.2})`
    ctx.beginPath()
    ctx.arc(sx + w / 2, yTop - 4, 3, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#a83838'
    if (lane.dir > 0) {
      ctx.beginPath()
      ctx.moveTo(sx + w - 4, yTop + h - 12)
      ctx.lineTo(sx + w, yTop + h - 7)
      ctx.lineTo(sx + w - 4, yTop + h - 2)
      ctx.closePath()
      ctx.fill()
    } else {
      ctx.beginPath()
      ctx.moveTo(sx + 4, yTop + h - 12)
      ctx.lineTo(sx, yTop + h - 7)
      ctx.lineTo(sx + 4, yTop + h - 2)
      ctx.closePath()
      ctx.fill()
    }
  } else if (lane.type === 'pontoon') {
    ctx.fillStyle = '#5a4520'
    ctx.fillRect(sx, yTop + 4, w, h - 8)
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(sx + 1, yTop + 5, w - 2, h - 10)
    ctx.fillStyle = '#1a1a1a'
    for (let i = 0; i < 4; i++) {
      ctx.fillRect(sx + 2 + i * 6, yTop + 6, 3, h - 12)
      ctx.fillRect(sx + w - 22 + i * 6, yTop + 6, 3, h - 12)
    }
    ctx.fillStyle = '#1a1a1a'
    ctx.font = 'bold 8px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.fillText('PONTOON', sx + w / 2, yTop + h / 2 + 3)
    ctx.fillStyle = 'rgba(0,0,0,0.4)'
    ctx.fillRect(sx, yTop + h - 4, w, 1)
  }
}

function drawBerths(
  ctx: CanvasRenderingContext2D,
  filled: boolean[],
  berthCols: number[],
  time: number,
): void {
  const y = rowY(ROW_HOME)
  ctx.fillStyle = '#0d1f3a'
  ctx.fillRect(0, y - 30, W, CELL + 30)

  ctx.fillStyle = 'rgba(60, 100, 160, 0.3)'
  ctx.fillRect(0, y - 30, W, 6)

  for (let i = 0; i < berthCols.length; i++) {
    const col = berthCols[i]
    const x = colX(col)
    drawBerth(ctx, x, y, filled[i], time, i)
  }
}

function drawBerth(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  isFilled: boolean,
  time: number,
  berthIdx: number,
): void {
  const cx = x + CELL / 2
  const yTop = y - 24
  ctx.fillStyle = isFilled ? '#3aa15a' : '#3a4458'
  ctx.beginPath()
  ctx.moveTo(x + 2, yTop + 18)
  ctx.lineTo(x + CELL - 2, yTop + 18)
  ctx.lineTo(x + CELL - 6, yTop + CELL)
  ctx.lineTo(x + 6, yTop + CELL)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = 'rgba(255,255,255,0.15)'
  ctx.fillRect(x + 4, yTop + 22, CELL - 8, 1.5)
  ctx.fillStyle = isFilled ? '#1a3a28' : '#2a3548'
  ctx.fillRect(cx - 6, yTop + 6, 12, 14)
  ctx.fillStyle = isFilled ? '#3a8fd6' : '#1a2236'
  ctx.fillRect(cx - 4, yTop + 8, 8, 2)
  ctx.fillRect(cx - 4, yTop + 12, 8, 2)

  if (isFilled) {
    ctx.fillStyle = '#d97a2c'
    ctx.fillRect(x + 6, yTop + 22, 8, 8)
    ctx.fillStyle = '#3a8fd6'
    ctx.fillRect(x + 16, yTop + 22, 8, 8)
    ctx.fillStyle = '#a83a72'
    ctx.fillRect(x + 26, yTop + 22, 8, 8)
    ctx.fillStyle = 'rgba(255,255,255,0.2)'
    ctx.fillRect(x + 6, yTop + 22, 8, 1)
    ctx.fillRect(x + 16, yTop + 22, 8, 1)
    ctx.fillRect(x + 26, yTop + 22, 8, 1)
    ctx.strokeStyle = '#3aa15a'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(cx - 6, yTop + 14)
    ctx.lineTo(cx - 2, yTop + 18)
    ctx.lineTo(cx + 6, yTop + 8)
    ctx.stroke()
  } else {
    const pulse = 0.4 + Math.sin(time * 0.15 + berthIdx) * 0.4
    ctx.fillStyle = `rgba(255, 209, 102, ${pulse})`
    ctx.font = 'bold 12px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.fillText('▼', cx, yTop + 14)
    ctx.fillStyle = 'rgba(80, 90, 110, 0.5)'
    ctx.fillRect(x + 6, yTop + 24, CELL - 12, 4)
    ctx.fillStyle = 'rgba(255, 209, 102, 0.6)'
    ctx.font = 'bold 7px ui-monospace, Menlo, monospace'
    ctx.fillText(`B${berthIdx + 1}`, cx, yTop + 32)
  }
}

function drawWorker(ctx: CanvasRenderingContext2D, w: Worker): void {
  let hopLift = 0
  if (w.hopFrames > 0) {
    const t = 1 - w.hopFrames / w.hopDuration
    hopLift = -Math.sin(t * Math.PI) * 8
  }

  if (w.dead) {
    const t = 1 - w.deathFrames / 60
    ctx.save()
    ctx.translate(w.x, w.y + hopLift)
    ctx.rotate(t * Math.PI * 2)
    drawWorkerBody(ctx, 0, true)
    ctx.restore()
    return
  }

  ctx.save()
  ctx.translate(w.x, w.y + hopLift)
  drawWorkerBody(ctx, w.facing, false)
  ctx.restore()
}

function drawWorkerBody(ctx: CanvasRenderingContext2D, facing: number, isDead: boolean): void {
  if (!isDead) {
    ctx.fillStyle = 'rgba(0,0,0,0.35)'
    ctx.beginPath()
    ctx.ellipse(0, 8, 10, 4, 0, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.fillStyle = '#ffd166'
  ctx.fillRect(-10, -5, 20, 14)
  ctx.fillStyle = 'rgba(255,255,255,0.6)'
  ctx.fillRect(-8, 0, 16, 1)
  ctx.fillRect(-8, 6, 16, 1)

  ctx.fillStyle = '#1a2236'
  ctx.fillRect(-6, 9, 5, 3)
  ctx.fillRect(1, 9, 5, 3)

  ctx.fillStyle = '#c9a648'
  ctx.beginPath()
  ctx.arc(0, -3, 8, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#8a6422'
  if (facing === 0) {
    ctx.fillRect(-7, -10, 14, 2)
  } else if (facing === 2) {
    ctx.fillRect(-7, 4, 14, 2)
  } else if (facing === 1) {
    ctx.fillRect(7, -7, 2, 8)
  } else if (facing === 3) {
    ctx.fillRect(-9, -7, 2, 8)
  }
  ctx.fillStyle = '#e8b347'
  ctx.beginPath()
  ctx.arc(-2, -5, 3, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = 'rgba(0,0,0,0.6)'
  if (facing === 0) {
    ctx.beginPath()
    ctx.moveTo(0, -10)
    ctx.lineTo(-2, -7)
    ctx.lineTo(2, -7)
    ctx.closePath()
    ctx.fill()
  } else if (facing === 2) {
    ctx.beginPath()
    ctx.moveTo(0, 4)
    ctx.lineTo(-2, 1)
    ctx.lineTo(2, 1)
    ctx.closePath()
    ctx.fill()
  } else if (facing === 1) {
    ctx.beginPath()
    ctx.moveTo(8, -3)
    ctx.lineTo(5, -5)
    ctx.lineTo(5, -1)
    ctx.closePath()
    ctx.fill()
  } else if (facing === 3) {
    ctx.beginPath()
    ctx.moveTo(-8, -3)
    ctx.lineTo(-5, -5)
    ctx.lineTo(-5, -1)
    ctx.closePath()
    ctx.fill()
  }
}

const styles: Record<string, CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    background: '#080e18',
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    color: '#cfd8e8',
    zIndex: 9999,
    overflow: 'hidden',
  },
  canvas: { width: '100%', height: '100%', display: 'block', touchAction: 'none' },
  hud: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '14px 22px',
    display: 'flex',
    gap: 18,
    alignItems: 'center',
    fontSize: 11,
    letterSpacing: '0.18em',
    pointerEvents: 'none',
    zIndex: 2,
    background: 'linear-gradient(to bottom, rgba(8,14,24,0.85), rgba(8,14,24,0))',
    flexWrap: 'wrap',
  },
  hudGroup: { display: 'flex', flexDirection: 'column', gap: 2 },
  hudLabel: { color: '#5a6478', fontSize: 9 },
  hudValue: { color: '#ffd166', fontSize: 13, letterSpacing: '0.1em' },
  timeBar: {
    width: 100,
    height: 8,
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid #2a3548',
  },
  timeFill: { height: '100%', transition: 'width 0.1s linear' },
  restart: {
    marginLeft: 'auto',
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 4,
    background: 'transparent',
    border: '1px solid #4a3a18',
    color: '#ffd166',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  close: {
    marginLeft: 8,
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 4,
    background: 'transparent',
    border: '1px solid #2a3548',
    color: '#7a8398',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: 'rgba(8, 14, 24, 0.7)',
    zIndex: 3,
    pointerEvents: 'none',
  },
  overlayTitle: {
    fontSize: 32,
    letterSpacing: '0.32em',
    color: '#ffd166',
  },
  overlaySub: { fontSize: 12, letterSpacing: '0.18em', color: '#7a8398' },
  overlaySubLarge: {
    fontSize: 13,
    letterSpacing: '0.22em',
    color: '#cfd8e8',
    marginBottom: 4,
    maxWidth: 420,
    textAlign: 'center',
  },
  legend: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 11,
    letterSpacing: '0.14em',
    color: '#cfd8e8',
    marginTop: 8,
  },
  legendRow: { textAlign: 'center' },
  controls: {
    marginTop: 16,
    fontSize: 10,
    letterSpacing: '0.18em',
    color: '#7a8398',
    textAlign: 'center',
    lineHeight: 1.6,
  },
  report: {
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid #2a3548',
    padding: '16px 22px',
    fontSize: 11,
    lineHeight: 1.7,
    color: '#cfd8e8',
    margin: 0,
    maxWidth: 380,
  },
}
